import {
  createEvTariffSchema,
  EV_PAYMENT_WARNING_LABELS,
  EV_SESSION_WARNING_LABELS,
  type EvAppliedLimit,
  type EvAvailabilityReport,
  type EvCommandStatus,
  type EvConnectorStatus,
  type EvCurrentTypeValue,
  type EvEnergyReport,
  type EvExportFieldKey,
  type EvExportProfile,
  type EvInvoiceStatus,
  type EvMeterPoint,
  type EvOperatorCommandType,
  type EvPaymentStatus,
  type EvSessionPhaseValue,
  type EvStationSettingsView,
  type EvTariffScope,
  type EvTariffView,
  saveEvStationSettingsSchema,
} from "@station/contracts";
import {
  addDays,
  Decimal,
  istanbulDateTimeToUtc,
  isValidLocalDate,
  type LocalDate,
  Money,
  toIstanbulDate,
} from "@station/core";
import type { Tone } from "@station/ui";
import { z } from "zod";
import {
  type ChartOption,
  formatChartNumber,
  GRID,
  LEGEND_POSITION,
  VALUE_AXIS_LABEL,
} from "../fuel/chart-options.js";
import { apiDecimalToTrText, trTextToApiDecimal, trTextToInt } from "../pos/pos-values.js";
import { describeOpeningWindow, issueText } from "../wash/wash-values.js";

/**
 * Şarj ekranlarının SAF yardımcıları (Prompt 19 web; birim testli): durum renkleri, güç / enerji / süre biçimleri, oturum
 * zaman çizelgesi ve ölçüm grafiği, tarife durumu ve özet metni, tarife / istasyon ayarı / dışa aktarım profili formlarının
 * istek gövdesine dönüşümü (sözleşme şemasıyla doğrulanır), komut yükü üreticileri ve rapor grafik seçenekleri.
 *
 * Para `Money`, miktar `Decimal` ile hesaplanır; grafiklerde yalnızca ÇİZİM anında sayıya çevrilir (AGENTS.md §3.2).
 */

// ── Durum renkleri (rozet METİN yazar; anlam yalnızca renkle taşınmaz) ─────────
export const CONNECTOR_STATUS_TONE: Record<EvConnectorStatus, Tone> = {
  Available: "success",
  Preparing: "info",
  Charging: "info",
  SuspendedEVSE: "warning",
  SuspendedEV: "warning",
  Finishing: "info",
  Reserved: "neutral",
  Unavailable: "neutral",
  Faulted: "danger",
};

export const SESSION_PHASE_TONE: Record<EvSessionPhaseValue, Tone> = {
  STARTED: "info",
  CHARGING: "info",
  FINISHING: "warning",
  COMPLETED: "success",
  FAULTED: "danger",
  RECOVERED: "warning",
};

export const PAYMENT_STATUS_TONE: Record<EvPaymentStatus, Tone> = {
  UNPAID: "warning",
  PAID: "success",
  COVERED: "info",
  BILLED: "info",
  FREE: "neutral",
};

export const INVOICE_STATUS_TONE: Record<EvInvoiceStatus, Tone> = {
  NONE: "neutral",
  PENDING: "warning",
  ISSUED: "success",
  FAILED: "danger",
};

export const COMMAND_STATUS_TONE: Record<EvCommandStatus, Tone> = {
  PENDING: "neutral",
  SENT: "info",
  ACCEPTED: "success",
  REJECTED: "danger",
  FAILED: "danger",
  TIMEOUT: "warning",
  EXPIRED: "neutral",
};

/** Sonuçlanmamış komut (kuyrukta / gönderildi): ekran kısa aralıkla yoklar. */
export function isCommandOpen(status: EvCommandStatus): boolean {
  return status === "PENDING" || status === "SENT";
}

/** Canlı (tamamlanmamış) oturum: araç bağlı, tutar kesinleşmedi. Kurtarılan oturum canlı sayılmaz (ADR 0019 §26). */
export function isSessionLive(phase: EvSessionPhaseValue): boolean {
  return phase === "STARTED" || phase === "CHARGING" || phase === "FINISHING" || phase === "FAULTED";
}

// ── Biçimler ───────────────────────────────────────────────────────────────────
/** Sondaki sıfırları atar: "120.00" → "120", "1.40" → "1.4". */
export function trimDecimal(value: string): string {
  return value.includes(".") ? value.replace(/0+$/, "").replace(/\.$/, "") : value;
}

/** Ondalık dizge → Türkiye biçimi, sondaki sıfırlar atılmış: "1.40" → "1,4", "120.00" → "120". */
export function formatTrimmed(value: string): string {
  const trimmed = trimDecimal(value);
  const scale = (trimmed.split(".")[1] ?? "").length;
  return Decimal.from(trimmed).formatTr(scale);
}

/** kW dizgesi (konnektör / şebeke gücü): "120.00" → "120 kW", "1.40" → "1,4 kW". */
export function formatKw(powerKw: string | null | undefined): string {
  if (powerKw === null || powerKw === undefined || powerKw === "") return "—";
  return `${formatTrimmed(powerKw)} kW`;
}

/** Watt (tam sayı) → kW, 1 ondalık: 11000 → "11 kW", 13333 → "13,3 kW". */
export function formatWatts(watts: number | null | undefined): string {
  if (watts === null || watts === undefined) return "—";
  return `${formatTrimmed(Decimal.from(watts).div(1000).round(1).toString())} kW`;
}

/** kWh dizgesi (3 ondalık): "19.200" → "19,200 kWh". */
export function formatKwh(value: string | null | undefined, scale = 3): string {
  if (value === null || value === undefined || value === "") return "—";
  return `${Decimal.from(value).formatTr(scale)} kWh`;
}

/** Yüzde dizgesi: "46.26" → "%46,26"; null → "—". */
export function formatPercent(value: string | null | undefined, scale = 1): string {
  if (value === null || value === undefined || value === "") return "—";
  return `%${Decimal.from(value).formatTr(scale)}`;
}

/** Tutar kaleminin miktarı birimiyle: KWH → "19,200 kWh", MINUTE → "106,083 dk". */
export function formatLineQuantity(quantity: string, unit: "KWH" | "MINUTE"): string {
  return `${Decimal.from(quantity).formatTr(3)} ${unit === "KWH" ? "kWh" : "dk"}`;
}

/** Birim fiyat (₺, 4 ondalık) birim başına: "8.9900" → "8,99 ₺/kWh". */
export function formatRate(price: string | null | undefined, unit: "KWH" | "MINUTE"): string {
  if (price === null || price === undefined || price === "") return "—";
  const decimals = Math.max(2, Math.min(4, (trimDecimal(price).split(".")[1] ?? "").length));
  return `${Decimal.from(price).formatTr(decimals)} ₺/${unit === "KWH" ? "kWh" : "dk"}`;
}

/** İki an arasındaki saniye (bitiş yoksa `now`); negatif olmaz. */
export function secondsBetween(from: string, to: string | null, now: number): number {
  const end = to === null ? now : Date.parse(to);
  return Math.max(0, Math.floor((end - Date.parse(from)) / 1000));
}

// ── Uyarılar ─────────────────────────────────────────────────────────────────
/** Oturum uyarı kodu → Türkçe etiket (çalışma zamanı + ödeme bağı); bilinmeyen kod olduğu gibi gösterilir. */
export function sessionWarningText(code: string): string {
  return (
    (EV_SESSION_WARNING_LABELS as Record<string, string>)[code] ??
    (EV_PAYMENT_WARNING_LABELS as Record<string, string>)[code] ??
    code
  );
}

/** Ödeme bağı uyarısı (kasa / iptal / çift ödeme) mı — elle düzeltme gerektirir, tehlike tonunda gösterilir. */
export function isPaymentWarning(code: string): boolean {
  return code in EV_PAYMENT_WARNING_LABELS;
}

// ── Oturum zaman çizelgesi ────────────────────────────────────────────────────
export const TIMELINE_STEPS = ["started", "chargingEnded", "stopped", "completed"] as const;
export type TimelineStep = (typeof TIMELINE_STEPS)[number];

export interface TimelineEntry {
  step: TimelineStep;
  at: string | null;
  /** Önceki adımdan bu adıma geçen saniye (ikisi de biliniyorsa). */
  sincePreviousSeconds: number | null;
}

/**
 * Başlangıç → şarj bitişi → durma (StopTransaction) → tamamlanma (araç çekildi). Durma şarj bitişiyle aynı anda olabilir;
 * kurtarılan oturumda bazı adımlar boştur. Adımlar her zaman bu sırayla döner (boş olan "bekleniyor" diye çizilir).
 */
export function sessionTimeline(session: {
  startedAt: string;
  chargingEndedAt: string | null;
  stoppedAt: string | null;
  completedAt: string | null;
}): TimelineEntry[] {
  const at: Record<TimelineStep, string | null> = {
    started: session.startedAt,
    chargingEnded: session.chargingEndedAt,
    stopped: session.stoppedAt,
    completed: session.completedAt,
  };
  let previous: string | null = null;
  return TIMELINE_STEPS.map((step) => {
    const value = at[step];
    const since =
      value !== null && previous !== null
        ? Math.max(0, Math.round((Date.parse(value) - Date.parse(previous)) / 1000))
        : null;
    if (value !== null) previous = value;
    return { step, at: value, sincePreviousSeconds: since };
  });
}

// ── Ölçüm serisi ─────────────────────────────────────────────────────────────
export interface MeterRow {
  at: string;
  /** Oturum başından beri enerji (kWh, 3 ondalık). */
  energyKwh: string | null;
  /** Anlık güç (kW, 2 ondalık). */
  powerKw: string | null;
  socPercent: string | null;
}

/**
 * Seyreltilmiş ölçüm serisi → tablo / grafik satırları. Enerji, başlangıç sayacından (yoksa ilk enerji örneğinden) farktır;
 * sayaç geri giderse 0'da kalır (çalışma zamanı da 0 sayar).
 */
export function meterRows(series: readonly EvMeterPoint[], meterStartWh: string | null): MeterRow[] {
  const base = meterStartWh ?? series.find((point) => point.energyWh !== null)?.energyWh ?? null;
  return series.map((point) => {
    let energyKwh: string | null = null;
    if (point.energyWh !== null && base !== null) {
      const delta = Decimal.from(point.energyWh).sub(base);
      energyKwh = (delta.isNegative() ? Decimal.from(0) : delta.div(1000)).toFixed(3);
    }
    return {
      at: point.at,
      energyKwh,
      powerKw: point.powerW === null ? null : Decimal.from(point.powerW).div(1000).toFixed(2),
      socPercent: point.socPercent,
    };
  });
}

export interface MeterChartLabels {
  power: string;
  energy: string;
  soc: string;
  limit: string;
  powerAxis: string;
  energyAxis: string;
}

const TR_TIME = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * Seri renkleri: temanın ilk iki rengi (birincil / bilgi) birbirine çok yakın; aynı grafikte yan yana çizilen seriler ayırt
 * edilsin diye sabit, açık ve koyu temada okunur tonlar (gösterge ayrıca METİN yazar; çizgi türleri de farklıdır).
 */
const SERIES_COLORS = {
  blue: "#2563eb",
  teal: "#0e7490",
  amber: "#d97706",
  violet: "#7c3aed",
  green: "#15803d",
} as const;

const numberOrNull = (value: string | null | undefined): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

/**
 * Oturum grafiği: güç (kW, sol eksen, basamaklı) ve oturum enerjisi (kWh, sağ eksen); yük yöneticisinin uyguladığı güç
 * sınırı sol eksende kesikli basamak çizgisi; SoC varsa ayrı (gizli ölçekli) eksende. Zaman ekseni İstanbul saati.
 */
export function meterChartOption(
  rows: readonly MeterRow[],
  limits: readonly Pick<EvAppliedLimit, "at" | "limitW" | "suspended">[],
  labels: MeterChartLabels,
): ChartOption {
  const hasSoc = rows.some((row) => row.socPercent !== null);
  const firstAt = rows[0]?.at ?? null;
  const lastAt = rows.at(-1)?.at ?? null;
  // Sınır ölçüm aralığına çizilir: ilk ölçümden önceki sınır ilk ölçümde başlar, son ölçümden sonrakiler çizilmez.
  const orderedLimits = [...limits]
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .filter((limit) => lastAt === null || Date.parse(limit.at) <= Date.parse(lastAt));
  const limitData: Array<[string, number]> = orderedLimits.map((limit) => [
    firstAt !== null && Date.parse(limit.at) < Date.parse(firstAt) ? firstAt : limit.at,
    limit.suspended ? 0 : limit.limitW / 1000,
  ]);
  const lastLimit = limitData.at(-1);
  if (lastLimit && lastAt && Date.parse(lastAt) > Date.parse(lastLimit[0]))
    limitData.push([lastAt, lastLimit[1]]);

  const series: NonNullable<ChartOption["series"]> = [
    {
      type: "line",
      name: labels.power,
      step: "end",
      showSymbol: false,
      yAxisIndex: 0,
      itemStyle: { color: SERIES_COLORS.blue },
      data: rows.map((row) => [row.at, numberOrNull(row.powerKw)]),
    },
    {
      type: "line",
      name: labels.energy,
      showSymbol: false,
      yAxisIndex: 1,
      itemStyle: { color: SERIES_COLORS.teal },
      areaStyle: { opacity: 0.12 },
      data: rows.map((row) => [row.at, numberOrNull(row.energyKwh)]),
    },
  ];
  if (limitData.length > 0)
    series.push({
      type: "line",
      name: labels.limit,
      step: "end",
      showSymbol: false,
      yAxisIndex: 0,
      itemStyle: { color: SERIES_COLORS.amber },
      lineStyle: { type: "dashed" },
      data: limitData,
    });
  if (hasSoc)
    series.push({
      type: "line",
      name: labels.soc,
      showSymbol: false,
      yAxisIndex: 2,
      itemStyle: { color: SERIES_COLORS.violet },
      lineStyle: { type: "dotted" },
      data: rows.map((row) => [row.at, numberOrNull(row.socPercent)]),
    });

  return {
    grid: GRID,
    tooltip: { trigger: "axis", valueFormatter: formatChartNumber },
    legend: {
      ...LEGEND_POSITION,
      data: series.map((entry) => String((entry as { name?: string }).name ?? "")),
    },
    xAxis: {
      type: "time",
      axisLabel: { hideOverlap: true, formatter: (value: number) => TR_TIME.format(value) },
    },
    yAxis: [
      { type: "value", name: labels.powerAxis, position: "left", axisLabel: VALUE_AXIS_LABEL },
      {
        type: "value",
        name: labels.energyAxis,
        position: "right",
        splitLine: { show: false },
        axisLabel: VALUE_AXIS_LABEL,
      },
      { type: "value", min: 0, max: 100, show: false },
    ],
    series,
  };
}

// ── Tarife ───────────────────────────────────────────────────────────────────
export type TariffState = "ACTIVE" | "SCHEDULED" | "EXPIRED" | "INACTIVE";

export const TARIFF_STATE_TONE: Record<TariffState, Tone> = {
  ACTIVE: "success",
  SCHEDULED: "info",
  EXPIRED: "neutral",
  INACTIVE: "neutral",
};

/** Yürürlük durumu: devre dışı > süresi dolmuş (bitiş hariç) > başlamamış > yürürlükte. */
export function tariffState(
  tariff: Pick<EvTariffView, "isActive" | "validFrom" | "validTo">,
  now: Date,
): TariffState {
  if (!tariff.isActive) return "INACTIVE";
  const at = now.getTime();
  if (tariff.validTo !== null && Date.parse(tariff.validTo) <= at) return "EXPIRED";
  if (Date.parse(tariff.validFrom) > at) return "SCHEDULED";
  return "ACTIVE";
}

/** Güç sınıfı özeti: "DC · ≥ 50 kW", "AC · 22–50 kW", "< 22 kW" (üst sınır hariçtir). */
export function powerClassText(
  tariff: Pick<EvTariffView, "currentType" | "minPowerKw" | "maxPowerKw">,
): string {
  const parts: string[] = [];
  if (tariff.currentType) parts.push(tariff.currentType);
  const min = tariff.minPowerKw === null ? null : formatTrimmed(tariff.minPowerKw);
  const max = tariff.maxPowerKw === null ? null : formatTrimmed(tariff.maxPowerKw);
  if (min !== null && max !== null) parts.push(`${min}–${max} kW`);
  else if (min !== null) parts.push(`≥ ${min} kW`);
  else if (max !== null) parts.push(`< ${max} kW`);
  return parts.join(" · ");
}

/** Saat bandının özeti: "Pzt–Cum 17:00–22:00 · 15,49 ₺/kWh" (boş gün listesi "Her gün"). `labels` ISO sırasıyla 7 kısa ad. */
export function describeBand(
  band: { weekdays: readonly number[]; from: string; to: string; energyPrice: string },
  labels: readonly string[],
  everyDay: string,
): string {
  return `${describeOpeningWindow(band, labels, everyDay)} · ${formatRate(band.energyPrice, "KWH")}`;
}

// ── Tarife formu ─────────────────────────────────────────────────────────────
export interface TariffBandRow {
  key: string;
  /** ISO hafta günleri (1 = Pazartesi … 7 = Pazar); boş = her gün. */
  weekdays: number[];
  from: string;
  to: string;
  /** Türkiye biçimli metin ("15,49"). */
  energyPrice: string;
}

export interface TariffFormValues {
  companyId: string;
  name: string;
  scopeKind: EvTariffScope;
  stationId: string;
  chargerId: string;
  currentType: "" | EvCurrentTypeValue;
  minPowerKw: string;
  maxPowerKw: string;
  validFromDate: string;
  validFromTime: string;
  validToDate: string;
  validToTime: string;
  /** Düzenlemede özgün anlar: tarih / saat değişmediyse saniyesiyle korunur. */
  originalValidFrom: string | null;
  originalValidTo: string | null;
  energyPrice: string;
  minutePrice: string;
  idlePrice: string;
  idleGraceMinutes: string;
  idleCap: string;
  energyProductId: string;
  timeProductId: string;
  idleProductId: string;
  bands: TariffBandRow[];
}

let bandCounter = 0;
export function newBandRow(partial: Partial<Omit<TariffBandRow, "key">> = {}): TariffBandRow {
  bandCounter += 1;
  return {
    key: `band-${bandCounter}`,
    weekdays: [],
    from: "00:00",
    to: "08:00",
    energyPrice: "",
    ...partial,
  };
}

const TR_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Istanbul",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** ISO an → İstanbul günü + "HH:MM". */
export function istanbulParts(iso: string): { date: LocalDate; time: string } {
  const instant = new Date(iso);
  return { date: toIstanbulDate(instant), time: TR_CLOCK.format(instant) };
}

/** İstanbul günü + saat → ISO (UTC). Geçersizse boş dizge (sözleşme şeması alanın altında hata üretir). */
export function istanbulIso(date: string, time: string): string {
  if (!isValidLocalDate(date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return "";
  return istanbulDateTimeToUtc(date as LocalDate, time).toISOString();
}

export function emptyTariffForm(companyId: string, today: LocalDate): TariffFormValues {
  return {
    companyId,
    name: "",
    scopeKind: "STATION",
    stationId: "",
    chargerId: "",
    currentType: "",
    minPowerKw: "",
    maxPowerKw: "",
    validFromDate: addDays(today, 1),
    validFromTime: "00:00",
    validToDate: "",
    validToTime: "00:00",
    originalValidFrom: null,
    originalValidTo: null,
    energyPrice: "",
    minutePrice: "0",
    idlePrice: "0",
    idleGraceMinutes: "0",
    idleCap: "",
    energyProductId: "",
    timeProductId: "",
    idleProductId: "",
    bands: [],
  };
}

export function tariffFormFrom(tariff: EvTariffView): TariffFormValues {
  const from = istanbulParts(tariff.validFrom);
  const to = tariff.validTo ? istanbulParts(tariff.validTo) : null;
  return {
    companyId: tariff.companyId,
    name: tariff.name,
    scopeKind: tariff.scopeKind,
    stationId: tariff.stationId ?? "",
    chargerId: tariff.chargerId ?? "",
    currentType: tariff.currentType ?? "",
    minPowerKw: apiDecimalToTrText(tariff.minPowerKw),
    maxPowerKw: apiDecimalToTrText(tariff.maxPowerKw),
    validFromDate: from.date,
    validFromTime: from.time,
    validToDate: to?.date ?? "",
    validToTime: to?.time ?? "00:00",
    originalValidFrom: tariff.validFrom,
    originalValidTo: tariff.validTo,
    energyPrice: apiDecimalToTrText(tariff.energyPrice),
    minutePrice: apiDecimalToTrText(tariff.minutePrice),
    idlePrice: apiDecimalToTrText(tariff.idlePrice),
    idleGraceMinutes: String(tariff.idleGraceMinutes),
    idleCap: apiDecimalToTrText(tariff.idleCap),
    energyProductId: tariff.energyProductId,
    timeProductId: tariff.timeProductId ?? "",
    idleProductId: tariff.idleProductId ?? "",
    bands: tariff.bands.map((band) =>
      newBandRow({
        weekdays: [...band.weekdays],
        from: band.from,
        to: band.to,
        energyPrice: apiDecimalToTrText(band.energyPrice),
      }),
    ),
  };
}

const nullIfEmpty = (value: string): string | null => (value.trim() === "" ? null : value);

function momentOf(date: string, time: string, original: string | null): string {
  if (original) {
    const parts = istanbulParts(original);
    if (parts.date === date && parts.time === time) return original;
  }
  return istanbulIso(date, time);
}

/**
 * Form → `createEvTariffSchema` girdisi (şirketsiz hâli `saveEvTariffSchema`'nın da gövdesidir). Kapsama göre ilgisiz
 * alanlar temizlenir (şirket tarifesinde istasyon, güç sınıfı dışında akım türü…); ücret sıfırsa ürün boşaltılmaz —
 * kullanıcının seçimi korunur. Bantlarda yedi gün seçiliyse "her gün" (boş dizi) yazılır.
 */
export function tariffBody(values: TariffFormValues): Record<string, unknown> {
  const scope = values.scopeKind;
  const powerClass = scope === "POWER_CLASS";
  return {
    companyId: values.companyId,
    name: values.name.trim(),
    scopeKind: scope,
    stationId: scope === "COMPANY" ? null : nullIfEmpty(values.stationId),
    chargerId: scope === "CHARGER" ? nullIfEmpty(values.chargerId) : null,
    currentType: powerClass && values.currentType !== "" ? values.currentType : null,
    minPowerKw: powerClass ? nullIfEmpty(trTextToApiDecimal(values.minPowerKw)) : null,
    maxPowerKw: powerClass ? nullIfEmpty(trTextToApiDecimal(values.maxPowerKw)) : null,
    validFrom: momentOf(values.validFromDate, values.validFromTime, values.originalValidFrom),
    validTo:
      values.validToDate.trim() === ""
        ? null
        : momentOf(values.validToDate, values.validToTime, values.originalValidTo),
    energyPrice: trTextToApiDecimal(values.energyPrice),
    minutePrice: trTextToApiDecimal(values.minutePrice) || "0",
    idlePrice: trTextToApiDecimal(values.idlePrice) || "0",
    idleGraceMinutes: trTextToInt(values.idleGraceMinutes === "" ? "0" : values.idleGraceMinutes),
    idleCap: nullIfEmpty(trTextToApiDecimal(values.idleCap)),
    energyProductId: values.energyProductId,
    timeProductId: nullIfEmpty(values.timeProductId),
    idleProductId: nullIfEmpty(values.idleProductId),
    bands: values.bands.map((band) => {
      const days = [...new Set(band.weekdays)].filter((day) => day >= 1 && day <= 7).sort((a, b) => a - b);
      return {
        weekdays: days.length === 7 ? [] : days,
        from: band.from.trim(),
        to: band.to.trim(),
        energyPrice: trTextToApiDecimal(band.energyPrice),
      };
    }),
  };
}

export type BandIssue = "time" | "order" | "overlap";
const BAND_FROM = /^([01]\d|2[0-3]):[0-5]\d$/;
const BAND_TO = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;

/** Bant sorunları (sunucunun `tariffBandIssues`'ıyla aynı ölçü): saat biçimi, bitiş ≤ başlangıç, aynı günde örtüşme. */
export function bandIssues(rows: readonly TariffBandRow[]): Record<string, BandIssue> {
  const issues: Record<string, BandIssue> = {};
  for (const row of rows) {
    if (!BAND_FROM.test(row.from.trim()) || !BAND_TO.test(row.to.trim())) issues[row.key] = "time";
    else if (row.from.trim() >= row.to.trim()) issues[row.key] = "order";
  }
  const days = (row: TariffBandRow) => (row.weekdays.length === 0 ? [1, 2, 3, 4, 5, 6, 7] : row.weekdays);
  for (let i = 0; i < rows.length; i += 1)
    for (let j = i + 1; j < rows.length; j += 1) {
      const a = rows[i]!;
      const b = rows[j]!;
      if (issues[a.key] || issues[b.key]) continue;
      const shared = days(a).some((day) => days(b).includes(day));
      if (shared && a.from < b.to && b.from < a.to) issues[b.key] = "overlap";
    }
  return issues;
}

/** Bant satırı sorunlarının iletisi (sözleşme iletileri gibi Türkçe; alanın altında gösterilir). */
export const BAND_ISSUE_TEXT: Record<BandIssue, string> = {
  time: "Saat SS:DD biçiminde olmalı (bitiş 24:00 olabilir)",
  order: "Bitiş başlangıçtan sonra olmalı",
  overlap: "Aynı günde başka bir bantla örtüşüyor",
};

/** Sözleşme gövdesindeki alan yolu → formdaki alan (tarih / saat ikilisi, bant satırı). */
export function tariffFormPath(path: string): string {
  if (path === "validFrom") return "validFromDate";
  if (path === "validTo") return "validToDate";
  return path;
}

/**
 * Tarife formunun şeması: kurallar SÖZLEŞMENİN `createEvTariffSchema`'sıdır (istemci ile sunucu aynı kuralı uygular);
 * alanlar Türkiye biçimli metindir, gövdeye `tariffBody` ile çevrilip doğrulanır. Bant örtüşmesi ayrıca denetlenir.
 */
export const tariffFormSchema = z
  .object({
    companyId: z.string(),
    name: z.string(),
    scopeKind: z.enum(["COMPANY", "STATION", "POWER_CLASS", "CHARGER"]),
    stationId: z.string(),
    chargerId: z.string(),
    currentType: z.enum(["", "AC", "DC"]),
    minPowerKw: z.string(),
    maxPowerKw: z.string(),
    validFromDate: z.string(),
    validFromTime: z.string(),
    validToDate: z.string(),
    validToTime: z.string(),
    originalValidFrom: z.string().nullable(),
    originalValidTo: z.string().nullable(),
    energyPrice: z.string(),
    minutePrice: z.string(),
    idlePrice: z.string(),
    idleGraceMinutes: z.string(),
    idleCap: z.string(),
    energyProductId: z.string(),
    timeProductId: z.string(),
    idleProductId: z.string(),
    bands: z.array(
      z.object({
        key: z.string(),
        weekdays: z.array(z.number().int()),
        from: z.string(),
        to: z.string(),
        energyPrice: z.string(),
      }),
    ),
  })
  .superRefine((values, context) => {
    const body = tariffBody(values as TariffFormValues);
    const result = createEvTariffSchema.safeParse(body);
    const seen = new Set<string>();
    // Boş bırakılmış zorunlu fiyat alanında sözleşmenin biçim iletisi yerine "zorunlu" yazılır.
    const blank = (path: string): boolean => {
      const value = path
        .split(".")
        .reduce<unknown>((node, part) => (node as Record<string, unknown> | undefined)?.[part], values);
      return typeof value === "string" && value.trim() === "";
    };
    if (!result.success)
      for (const issue of result.error.issues) {
        const path = tariffFormPath(issue.path.map(String).join("."));
        if (seen.has(path)) continue;
        seen.add(path);
        const message = /energyPrice$/.test(path) && blank(path) ? "Bu alan zorunludur" : issueText(issue);
        context.addIssue({ code: "custom", path: path.split("."), message });
      }
    const rows = values.bands as TariffBandRow[];
    const issues = bandIssues(rows);
    rows.forEach((row, index) => {
      const issue = issues[row.key];
      if (issue && !seen.has(`bands.${index}.from`))
        context.addIssue({ code: "custom", path: ["bands", index, "from"], message: BAND_ISSUE_TEXT[issue] });
    });
  });

// ── Tarife deneme ────────────────────────────────────────────────────────────
export interface PreviewFormValues {
  chargerId: string;
  connectorNo: string;
  date: string;
  time: string;
  energyKwh: string;
  chargingMinutes: string;
  idleMinutes: string;
  coveredKwh: string;
}

export function previewBody(values: PreviewFormValues, tariffId: string | null): Record<string, unknown> {
  return {
    chargerId: values.chargerId,
    connectorNo: trTextToInt(values.connectorNo),
    startedAt: istanbulIso(values.date, values.time),
    energyKwh: trTextToApiDecimal(values.energyKwh),
    chargingMinutes: trTextToApiDecimal(values.chargingMinutes),
    idleMinutes: trTextToApiDecimal(values.idleMinutes) || "0",
    coveredKwh: trTextToApiDecimal(values.coveredKwh) || "0",
    tariffId,
  };
}

// ── İstasyon şarj ayarı ─────────────────────────────────────────────────────
export interface SettingsFormValues {
  operationModel: EvStationSettingsView["operationModel"];
  partnerCode: string;
  partnerStationRef: string;
  /** "anahtar=değer" satırları. */
  partnerConfigText: string;
  maxGridKw: string;
  loadMethod: EvStationSettingsView["loadMethod"];
  safetyMarginPercent: string;
  minSessionPowerKw: string;
  priorityFleet: string;
  prioritySubscription: string;
  priorityApp: string;
  unknownIdTagPolicy: EvStationSettingsView["unknownIdTagPolicy"];
  electricityMeterId: string;
}

export function partnerConfigText(config: Record<string, string>): string {
  return Object.entries(config)
    .map(([key, value]) => `${key}=${value}`)
    .join("\n");
}

/** "anahtar=değer" satırları → nesne. Boş satır atlanır; "=" içermeyen satır `null` döndürür (form hatası). */
export function parsePartnerConfig(text: string): Record<string, string> | null {
  const result: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (trimmed === "") continue;
    const index = trimmed.indexOf("=");
    if (index <= 0) return null;
    result[trimmed.slice(0, index).trim()] = trimmed.slice(index + 1).trim();
  }
  return result;
}

export function settingsFormFrom(settings: EvStationSettingsView): SettingsFormValues {
  return {
    operationModel: settings.operationModel,
    partnerCode: settings.partnerCode ?? "",
    partnerStationRef: settings.partnerStationRef ?? "",
    partnerConfigText: partnerConfigText(settings.partnerConfig),
    maxGridKw: apiDecimalToTrText(settings.maxGridKw === null ? null : trimDecimal(settings.maxGridKw)),
    loadMethod: settings.loadMethod,
    safetyMarginPercent: apiDecimalToTrText(trimDecimal(settings.safetyMarginPercent)),
    minSessionPowerKw: apiDecimalToTrText(trimDecimal(settings.minSessionPowerKw)),
    priorityFleet: String(settings.priorityFleet),
    prioritySubscription: String(settings.prioritySubscription),
    priorityApp: String(settings.priorityApp),
    unknownIdTagPolicy: settings.unknownIdTagPolicy,
    electricityMeterId: settings.electricityMeterId ?? "",
  };
}

/** Form → `saveEvStationSettingsSchema` gövdesi (ayar TAMAMEN yazılır; `version` iyimser kilit). */
export function settingsBody(values: SettingsFormValues, version: number): Record<string, unknown> {
  const partner = values.operationModel === "NETWORK_PARTNER";
  return {
    operationModel: values.operationModel,
    partnerCode: partner ? nullIfEmpty(values.partnerCode.trim().toUpperCase()) : null,
    partnerStationRef: partner ? nullIfEmpty(values.partnerStationRef.trim()) : null,
    partnerConfig: partner ? (parsePartnerConfig(values.partnerConfigText) ?? {}) : {},
    maxGridKw: nullIfEmpty(trTextToApiDecimal(values.maxGridKw)),
    loadMethod: values.loadMethod,
    safetyMarginPercent: trTextToApiDecimal(values.safetyMarginPercent),
    minSessionPowerKw: trTextToApiDecimal(values.minSessionPowerKw),
    priorityFleet: trTextToInt(values.priorityFleet),
    prioritySubscription: trTextToInt(values.prioritySubscription),
    priorityApp: trTextToInt(values.priorityApp),
    unknownIdTagPolicy: values.unknownIdTagPolicy,
    electricityMeterId: nullIfEmpty(values.electricityMeterId),
    version,
  };
}

/** Ayar formunun şeması: kurallar sözleşmenin `saveEvStationSettingsSchema`'sıdır (+ ortak ayar satırlarının biçimi). */
export function settingsFormSchema(version: number) {
  return z
    .object({
      operationModel: z.enum(["OWN_CSMS", "NETWORK_PARTNER"]),
      partnerCode: z.string(),
      partnerStationRef: z.string(),
      partnerConfigText: z.string(),
      maxGridKw: z.string(),
      loadMethod: z.enum(["OFF", "EQUAL", "PRIORITY"]),
      safetyMarginPercent: z.string(),
      minSessionPowerKw: z.string(),
      priorityFleet: z.string(),
      prioritySubscription: z.string(),
      priorityApp: z.string(),
      unknownIdTagPolicy: z.enum(["REJECT", "ACCEPT_POS_PAYMENT"]),
      electricityMeterId: z.string(),
    })
    .superRefine((values, context) => {
      const result = saveEvStationSettingsSchema.safeParse(settingsBody(values, version));
      const seen = new Set<string>();
      if (!result.success)
        for (const issue of result.error.issues) {
          const path = issue.path.map(String).join(".");
          const field = path.startsWith("partnerConfig") ? "partnerConfigText" : path;
          if (seen.has(field)) continue;
          seen.add(field);
          context.addIssue({ code: "custom", path: [field], message: issueText(issue) });
        }
      if (
        values.operationModel === "NETWORK_PARTNER" &&
        parsePartnerConfig(values.partnerConfigText) === null &&
        !seen.has("partnerConfigText")
      )
        context.addIssue({
          code: "custom",
          path: ["partnerConfigText"],
          message: "Her satır anahtar=değer biçiminde olmalı",
        });
    });
}

/** Paylaştırılabilir güç (W) → kW metni; yük yönetimi kapalıysa null. */
export function availablePowerText(availableW: number | null): string | null {
  return availableW === null ? null : formatWatts(availableW);
}

/** Canlı çekiş / kullanılabilir güç oranı (%, 0–100 arası kırpılır; 1 ondalık). */
export function loadPercent(drawingW: number, availableW: number | null): string | null {
  if (availableW === null || availableW <= 0) return null;
  const percent = Decimal.from(drawingW).mul(100).div(availableW).round(1);
  return (percent.gt(100) ? Decimal.from(100) : percent).toString();
}

// ── Dışa aktarım profili ─────────────────────────────────────────────────────
export interface ExportFieldRow {
  key: EvExportFieldKey;
  label: string;
}

export interface ExportProfileFormValues {
  code: string;
  name: string;
  format: "CSV" | "JSON";
  csvDelimiter: ";" | "," | "|" | "\t";
  isActive: boolean;
  fields: ExportFieldRow[];
}

export function emptyExportProfileForm(
  available: readonly { key: EvExportFieldKey; defaultLabel: string }[],
): ExportProfileFormValues {
  return {
    code: "",
    name: "",
    format: "CSV",
    csvDelimiter: ";",
    isActive: true,
    fields: available
      .filter((field) =>
        ["sessionId", "startedAt", "completedAt", "energyKwh", "totalAmount"].includes(field.key),
      )
      .map((field) => ({ key: field.key, label: field.defaultLabel })),
  };
}

export function exportProfileFormFrom(profile: EvExportProfile): ExportProfileFormValues {
  return {
    code: profile.code,
    name: profile.name,
    format: profile.format,
    csvDelimiter: profile.csvDelimiter,
    isActive: profile.isActive,
    fields: profile.fields.map((field) => ({ key: field.key, label: field.label })),
  };
}

export function createExportProfileBody(values: ExportProfileFormValues): Record<string, unknown> {
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    format: values.format,
    csvDelimiter: values.csvDelimiter,
    fields: values.fields.map((field) => ({ key: field.key, label: field.label.trim() })),
  };
}

export function saveExportProfileBody(
  values: ExportProfileFormValues,
  version: number,
): Record<string, unknown> {
  const { code: _code, ...rest } = createExportProfileBody(values);
  return { ...rest, isActive: values.isActive, version };
}

/** Alanı listede bir yukarı / aşağı taşır (sütun sırası); sınırda değişmez. */
export function moveField<T>(rows: readonly T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction;
  if (target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/**
 * Yedek dosya adı: sunucunun `Content-Disposition`'ındakiyle aynı kalıp. Asıl ad yanıt başlığından okunur
 * (`fileNameFromContentDisposition`); başlık yoksa / okunamazsa (ör. ara vekil CORS başlığını düşürdüyse) bu kullanılır.
 */
export function exportFileName(
  profile: Pick<EvExportProfile, "code" | "format">,
  from: string,
  to: string,
): string {
  return `sarj-oturumlari-${profile.code}-${from}-${to}.${profile.format === "CSV" ? "csv" : "json"}`;
}

/**
 * `Content-Disposition` başlığındaki dosya adı (RFC 6266): `filename*=UTF-8''…` (yüzde kodlu) önce, sonra `filename="…"` /
 * tırnaksız `filename=…`. Yol ayraçları ve denetim karakterleri atılır (tarayıcı zaten yalnızca adı kullanır); başlık yoksa,
 * ad boşsa ya da çözülemiyorsa `null` — çağıran yerel kalıba (`exportFileName`) düşer.
 */
export function fileNameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const clean = (name: string): string | null => {
    const base = Array.from(name)
      .filter((char) => char.charCodeAt(0) > 0x1f && char.charCodeAt(0) !== 0x7f)
      .join("")
      .split(/[\\/]/)
      .pop()!
      .trim();
    return base === "" || base === "." || base === ".." ? null : base;
  };
  const extended = /filename\*\s*=\s*([^;]+)/i.exec(header)?.[1]?.trim();
  if (extended) {
    const match = /^([\w!#$%&+^`{}~-]+)'[^']*'(.+)$/.exec(extended.replace(/^"|"$/g, ""));
    if (match && match[1]!.toLowerCase() === "utf-8") {
      try {
        const decoded = clean(decodeURIComponent(match[2]!));
        if (decoded) return decoded;
      } catch {
        // Bozuk yüzde kodlaması: düz `filename`'e düşülür.
      }
    }
  }
  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(header)?.[1];
  if (quoted !== undefined) return clean(quoted.replace(/\\(.)/g, "$1"));
  const bare = /filename\s*=\s*([^;\s]+)/i.exec(header)?.[1];
  return bare ? clean(bare) : null;
}

// ── Komutlar ─────────────────────────────────────────────────────────────────
export interface CommandFormValues {
  type: EvOperatorCommandType;
  resetType: "Soft" | "Hard";
  connectorId: string;
  availability: "Operative" | "Inoperative";
  keys: string;
  key: string;
  value: string;
  location: string;
  retrieveDate: string;
  retrieveTime: string;
  startDate: string;
  stopDate: string;
}

export function emptyCommandForm(type: EvOperatorCommandType, today: LocalDate): CommandFormValues {
  return {
    type,
    resetType: "Soft",
    connectorId: "1",
    availability: "Inoperative",
    keys: "",
    key: "",
    value: "",
    location: "",
    retrieveDate: today,
    retrieveTime: "03:00",
    startDate: addDays(today, -1),
    stopDate: today,
  };
}

/** `https://…` / `ftp://…` adresi (OCPP `anyURI`); sunucu yükü ayrıca doğrular. */
export function isUrl(text: string): boolean {
  try {
    const url = new URL(text.trim());
    return /^(https?|ftps?|sftp):$/.test(url.protocol);
  } catch {
    return false;
  }
}

export type CommandFormIssue = "connector" | "key" | "location" | "date";

/**
 * Form → OCPP 1.6 isteği (`POST /ev/chargers/{id}/commands` `payload`). Konnektör "0" ünitenin kendisidir (yalnızca
 * erişilebilirlik ve güç sınırı temizleme); kilit açma konnektör ister. Hata varsa alan adı döner.
 */
export function commandPayload(
  values: CommandFormValues,
): { ok: true; payload: Record<string, unknown> } | { ok: false; issue: CommandFormIssue } {
  const connector = Number.parseInt(values.connectorId, 10);
  switch (values.type) {
    case "Reset":
      return { ok: true, payload: { type: values.resetType } };
    case "UnlockConnector":
      return Number.isInteger(connector) && connector >= 1
        ? { ok: true, payload: { connectorId: connector } }
        : { ok: false, issue: "connector" };
    case "ChangeAvailability":
      return Number.isInteger(connector) && connector >= 0
        ? { ok: true, payload: { connectorId: connector, type: values.availability } }
        : { ok: false, issue: "connector" };
    case "ClearChargingProfile":
      return values.connectorId === "" || values.connectorId === "all"
        ? { ok: true, payload: {} }
        : Number.isInteger(connector) && connector >= 0
          ? { ok: true, payload: { connectorId: connector } }
          : { ok: false, issue: "connector" };
    case "GetConfiguration": {
      const keys = values.keys
        .split(/[\s,]+/)
        .map((key) => key.trim())
        .filter((key) => key !== "");
      return { ok: true, payload: keys.length > 0 ? { key: keys } : {} };
    }
    case "ChangeConfiguration":
      return values.key.trim() === "" || values.key.trim().length > 50
        ? { ok: false, issue: "key" }
        : { ok: true, payload: { key: values.key.trim(), value: values.value } };
    case "UpdateFirmware": {
      if (!isUrl(values.location)) return { ok: false, issue: "location" };
      const retrieveDate = istanbulIso(values.retrieveDate, values.retrieveTime);
      return retrieveDate === ""
        ? { ok: false, issue: "date" }
        : { ok: true, payload: { location: values.location.trim(), retrieveDate } };
    }
    case "GetDiagnostics": {
      if (!isUrl(values.location)) return { ok: false, issue: "location" };
      const payload: Record<string, unknown> = { location: values.location.trim() };
      if (values.startDate !== "") {
        const start = istanbulIso(values.startDate, "00:00");
        if (start === "") return { ok: false, issue: "date" };
        payload.startTime = start;
      }
      if (values.stopDate !== "") {
        const stop = istanbulIso(addDays(values.stopDate as LocalDate, 1), "00:00");
        if (stop === "" || !isValidLocalDate(values.stopDate)) return { ok: false, issue: "date" };
        payload.stopTime = stop;
      }
      return { ok: true, payload };
    }
  }
}

/** Komut yükünün okunur özeti (listede): "Soft", "Konnektör 2 · Inoperative", anahtar listesi… */
export function commandPayloadText(payload: Record<string, unknown>): string {
  const parts: string[] = [];
  if (typeof payload.connectorId === "number") parts.push(`#${payload.connectorId}`);
  if (typeof payload.type === "string") parts.push(payload.type);
  if (typeof payload.transactionId === "number") parts.push(`tx ${payload.transactionId}`);
  if (Array.isArray(payload.key)) parts.push(payload.key.join(", "));
  else if (typeof payload.key === "string")
    parts.push(`${payload.key}=${typeof payload.value === "string" ? payload.value : ""}`);
  if (typeof payload.location === "string") parts.push(payload.location);
  const profile = payload.csChargingProfiles as
    { chargingSchedule?: { chargingSchedulePeriod?: Array<{ limit?: number }> } } | undefined;
  const limit = profile?.chargingSchedule?.chargingSchedulePeriod?.[0]?.limit;
  if (typeof limit === "number") parts.push(formatWatts(limit));
  return parts.join(" · ");
}

export type CommandResponseSummary =
  | { kind: "status"; status: string }
  | { kind: "configuration"; count: number }
  | { kind: "file"; fileName: string };

/** Komut yanıtının özeti: `{ status }` → durum; yapılandırma yanıtı → anahtar sayısı; tanılama → dosya adı. */
export function commandResponseSummary(response: unknown): CommandResponseSummary | null {
  if (response === null || response === undefined || typeof response !== "object") return null;
  const value = response as Record<string, unknown>;
  if (typeof value.status === "string") return { kind: "status", status: value.status };
  if (Array.isArray(value.configurationKey))
    return { kind: "configuration", count: value.configurationKey.length };
  if (typeof value.fileName === "string") return { kind: "file", fileName: value.fileName };
  return null;
}

// ── Raporlar ─────────────────────────────────────────────────────────────────
/** Varsayılan aralık: son 30 gün (bugün dâhil). */
export function defaultEvRange(today: LocalDate): { from: LocalDate; to: LocalDate } {
  return { from: addDays(today, -29), to: today };
}

const moneyNumber = (minor: string): number => Number(Money.fromMinor(minor).toMajorString());

export interface EnergyChartLabels {
  energy: string;
  revenue: string;
  cost: string;
  margin: string;
  energyAxis: string;
  moneyAxis: string;
}

/** Günlük enerji (kWh, sütun, sol eksen) + KDV hariç gelir / enerji maliyeti / marj (₺, çizgi, sağ eksen). */
export function energyByDayChartOption(
  byDay: EvEnergyReport["byDay"],
  labels: EnergyChartLabels,
): ChartOption {
  // "2026-09-26" → "26.09" (Intl'in tr-TR gün / ay biçimi ortama göre "26/09" verebiliyor; eksen Türkiye biçiminde kalsın).
  const days = byDay.map((row) => `${row.date.slice(8, 10)}.${row.date.slice(5, 7)}`);
  return {
    grid: GRID,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: [labels.energy, labels.revenue, labels.cost, labels.margin] },
    xAxis: { type: "category", data: days, axisLabel: { hideOverlap: true } },
    yAxis: [
      { type: "value", name: labels.energyAxis, axisLabel: VALUE_AXIS_LABEL },
      { type: "value", name: labels.moneyAxis, splitLine: { show: false }, axisLabel: VALUE_AXIS_LABEL },
    ],
    series: [
      {
        type: "bar",
        name: labels.energy,
        yAxisIndex: 0,
        barMaxWidth: 18,
        itemStyle: { color: SERIES_COLORS.teal },
        data: byDay.map((row) => Number(row.energyKwh)),
      },
      {
        type: "line",
        name: labels.revenue,
        yAxisIndex: 1,
        showSymbol: false,
        itemStyle: { color: SERIES_COLORS.blue },
        data: byDay.map((row) => moneyNumber(row.revenueNet)),
      },
      {
        type: "line",
        name: labels.cost,
        yAxisIndex: 1,
        showSymbol: false,
        itemStyle: { color: SERIES_COLORS.amber },
        lineStyle: { type: "dashed" },
        data: byDay.map((row) => moneyNumber(row.energyCost)),
      },
      {
        type: "line",
        name: labels.margin,
        yAxisIndex: 1,
        showSymbol: false,
        itemStyle: { color: SERIES_COLORS.green },
        lineStyle: { type: "dotted" },
        data: byDay.map((row) => moneyNumber(row.margin)),
      },
    ],
  };
}

export const AVAILABILITY_BUCKETS = [
  "AVAILABLE",
  "OCCUPIED",
  "RESERVED",
  "FAULTED",
  "UNAVAILABLE",
  "OFFLINE",
] as const;
export type AvailabilityBucket = (typeof AVAILABILITY_BUCKETS)[number];

/** Kova renkleri: boş yeşil, dolu mavi, arıza kırmızı, kopukluk gri (gösterge METİN yazar). */
const BUCKET_COLORS: Record<AvailabilityBucket, string> = {
  AVAILABLE: "#15803d",
  OCCUPIED: "#0e7490",
  RESERVED: "#7c3aed",
  FAULTED: "#b91c1c",
  UNAVAILABLE: "#b45309",
  OFFLINE: "#6b7280",
};

/** Saniye → saat (1 ondalık) — yalnızca çizim. */
const hours = (seconds: number): number => Math.round((seconds / 3600) * 10) / 10;

/** Konnektör başına durum kovaları (saat, yığılmış yatay çubuk). */
export function availabilityChartOption(
  connectors: EvAvailabilityReport["connectors"],
  labels: {
    buckets: Record<AvailabilityBucket, string>;
    hoursAxis: string;
    hoursUnit: string;
    connector: (no: number) => string;
  },
): ChartOption {
  const rows = [...connectors].reverse();
  return {
    grid: GRID,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatChartNumber(value)} ${labels.hoursUnit}`,
    },
    legend: { ...LEGEND_POSITION, data: AVAILABILITY_BUCKETS.map((bucket) => labels.buckets[bucket]) },
    xAxis: { type: "value", name: labels.hoursAxis, axisLabel: VALUE_AXIS_LABEL },
    yAxis: {
      type: "category",
      data: rows.map((row) => `${row.chargerName} · ${labels.connector(row.connectorNo)}`),
      axisLabel: { width: 160, overflow: "truncate" },
    },
    series: AVAILABILITY_BUCKETS.map((bucket) => ({
      type: "bar" as const,
      name: labels.buckets[bucket],
      stack: "buckets",
      barMaxWidth: 24,
      itemStyle: { color: BUCKET_COLORS[bucket] },
      data: rows.map((row) => hours(row.seconds[bucket])),
    })),
  };
}
